import { useEffect, useRef } from 'react';

/**
 * Banner "¿Necesitás ayuda con tu plan?" que invita a escribir por WhatsApp a
 * un asesor. Se muestra al final del paso de selección de plan (después del
 * MS2). Incluye una simulación animada de chat en la foto de la derecha;
 * respeta `prefers-reduced-motion`. Los estilos viven en `globals.css` (.wbA*).
 */

const WA_HREF =
  'https://api.whatsapp.com/send/?phone=5491123935555&text=Hola%2C%20necesito%20ayuda%20con%20mi%20plan.&type=phone_number&app_absent=0';

// Foto de fondo (CDN externo de Webflow, tal cual el diseño original).
const WA_PHOTO =
  'https://cdn.prod.website-files.com/6841ec68b99280a8ee317172/693082bf33502d88bb435b6f_cuota-fija-form.avif';

const WA_ICON_PATH =
  'M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z';

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={WA_ICON_PATH} />
    </svg>
  );
}

export function WhatsAppBanner({ href = WA_HREF }: { href?: string }) {
  const chatRef = useRef<HTMLDivElement>(null);

  // Simulación de chat (verde = usuario / blanco = agente). Loop cada ~8.8s.
  // Port directo del script original con limpieza de timers al desmontar.
  useEffect(() => {
    const chat = chatRef.current;
    if (!chat) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    const bubble = (side: 'in' | 'out', text: string) => {
      const b = document.createElement('div');
      b.className = `wbA-msg wbA-${side}`;
      b.textContent = text;
      chat.appendChild(b);
      return b;
    };
    const typing = () => {
      const t = document.createElement('div');
      t.className = 'wbA-msg wbA-typing';
      t.innerHTML = '<span></span><span></span><span></span>';
      chat.appendChild(t);
      return t;
    };

    let timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, ms));
    };
    const clearTimers = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };

    const run = () => {
      clearTimers();
      chat.innerHTML = '';
      at(400, () => bubble('out', 'Hola, ¿cómo me doy de alta? 👋'));
      let t1: HTMLDivElement | undefined;
      at(1400, () => {
        t1 = typing();
      });
      at(2700, () => {
        t1?.remove();
        bubble('in', '¡Hola! Te ayudo ahora mismo 😊');
      });
      let t2: HTMLDivElement | undefined;
      at(3800, () => {
        t2 = typing();
      });
      at(5100, () => {
        t2?.remove();
        bubble('in', 'Es 100% online, en 5 min ✅');
      });
      at(8800, run);
    };

    if (reduce) {
      bubble('out', 'Hola, ¿cómo me doy de alta? 👋');
      bubble('in', '¡Hola! Te ayudo ahora mismo 😊');
      bubble('in', 'Es 100% online, en 5 min ✅');
    } else {
      run();
    }

    return () => {
      clearTimers();
      chat.innerHTML = '';
    };
  }, []);

  return (
    <a
      className="wbA"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Escribinos por WhatsApp"
    >
      <div className="wbA-left">
        <h3 className="wbA-title">
          ¿NECESITÁS AYUDA
          <br />
          CON TU PLAN?
        </h3>
        <p className="wbA-sub">
          Resolvé todo por WhatsApp con un asesor. Rápido, sin llamadas y sin esperas.
        </p>
        <span className="wbA-cta">
          <WaIcon />
          Consultá por WhatsApp
        </span>
      </div>
      <div className="wbA-media" aria-hidden="true">
        <div className="wbA-photo" style={{ backgroundImage: `url('${WA_PHOTO}')` }}>
          <span className="wbA-walogo">
            <WaIcon />
          </span>
          <div className="wbA-chat" ref={chatRef} />
        </div>
      </div>
    </a>
  );
}
